import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Switch from '@alltrails/denali/components/Switch';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import RotateLeft from '@alltrails/denali/icons/RotateLeft';
import RotateRight from '@alltrails/denali/icons/RotateRight';
import DoubleBack from '@alltrails/denali/icons/DoubleBack';
import CloseLoop from '@alltrails/denali/icons/CloseLoop';
import Reverse from '@alltrails/denali/icons/Reverse';
import SparkleFilled from '@alltrails/denali/icons/SparkleFilled';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import Collapse from '@alltrails/denali/icons/Collapse';
import Elevation from '@alltrails/denali/icons/Elevation';
import Wildflowers from '@alltrails/denali/icons/Wildflowers';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import logCustomRouteSmartRoutingMenuTapped from '@alltrails/amplitude/events/logCustomRouteSmartRoutingMenuTapped';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logCustomRouteEditorUndoCtaTapped from '@alltrails/amplitude/events/logCustomRouteEditorUndoCtaTapped';
import { useAppDispatch, useAppSelector } from '../../redux';
import { setIsSmartRouting } from '../../redux/customRoute';
import useSegmentActions from '../CustomRoute/useSegmentActions';
import useAdvancedRouting from '../CustomRoute/useAdvancedRouting';
import styles from './CustomRouteToolbar.module.scss';

type CustomRouteToolbarProps = {
  className?: string;
};

const CustomRouteToolbar = ({ className }: CustomRouteToolbarProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isSmartRouting = useAppSelector(state => state.customRoute.isSmartRouting);
  const { isUndoDisabled, isRedoDisabled, isReverseDisabled, isDoubleBackDisabled, isCloseLoopDisabled, undo, redo, reverse, doubleBack, closeLoop } =
    useSegmentActions();
  const { isDisabled: isOptionsDisabled, makeShorter, leastGain, mostScenic } = useAdvancedRouting();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const options = useMemo(
    () => [
      {
        testId: 'dropdown-menu-options-make-shorter',
        title: intl.formatMessage({ defaultMessage: 'Make shorter' }),
        leadingIcon: { icon: { Component: Collapse } },
        onClick: () => {
          makeShorter();
          setIsOptionsOpen(false);
        }
      },
      {
        testId: 'dropdown-menu-options-elevation',
        title: intl.formatMessage({ defaultMessage: 'Reduce elevation gain' }),
        leadingIcon: { icon: { Component: Elevation } },
        onClick: () => {
          leastGain();
          setIsOptionsOpen(false);
        }
      },
      {
        testId: 'dropdown-menu-options-wildflowers',
        title: intl.formatMessage({ defaultMessage: 'Take the scenic route' }),
        leadingIcon: { icon: { Component: Wildflowers } },
        onClick: () => {
          mostScenic();
          setIsOptionsOpen(false);
        }
      }
    ],
    [intl, makeShorter, leastGain, mostScenic]
  );

  const handleOpenOptions = useCallback(() => {
    if (!isOptionsOpen) {
      logCustomRouteSmartRoutingMenuTapped();
    }
    setIsOptionsOpen(!isOptionsOpen);
  }, [isOptionsOpen]);

  const onSmartRoutingChange = useCallback(
    (newIsSmartRouting: boolean) => {
      dispatch(setIsSmartRouting(newIsSmartRouting));
      logCustomRouteUpdated({
        entry_point_location: newIsSmartRouting ? 'snap to paths on' : 'snap to paths off',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: true
      });
    },
    [dispatch, isSmartRouting]
  );

  const handleUndo = useCallback(() => {
    undo();
    logCustomRouteEditorUndoCtaTapped();
  }, [undo]);

  return (
    <div className={classNames(styles.container, className)}>
      <Switch
        testId="switch-routing"
        labelText={intl.formatMessage({ defaultMessage: 'Snap to paths' })}
        selected={isSmartRouting}
        onChange={onSmartRoutingChange}
      />
      <div className={styles.divider} />
      <IconButtonWithTooltip
        testId="button-undo"
        icon={{ Component: RotateLeft }}
        title={intl.formatMessage({ defaultMessage: 'Undo' })}
        onClick={handleUndo}
        disabled={isUndoDisabled}
        variant="flat"
        size="sm"
      />
      <IconButtonWithTooltip
        testId="button-redo"
        icon={{ Component: RotateRight }}
        title={intl.formatMessage({ defaultMessage: 'Redo' })}
        onClick={() => redo('toolbar')}
        disabled={isRedoDisabled}
        variant="flat"
        size="sm"
      />
      <div className={styles.divider} />
      <IconButtonWithTooltip
        testId="button-double-back"
        icon={{ Component: DoubleBack }}
        title={intl.formatMessage({ defaultMessage: 'Double back' })}
        onClick={() => doubleBack('toolbar')}
        disabled={isDoubleBackDisabled}
        variant="flat"
        size="sm"
      />
      <IconButtonWithTooltip
        testId="button-close-loop"
        icon={{ Component: CloseLoop }}
        title={intl.formatMessage({ defaultMessage: 'Close loop' })}
        onClick={() => closeLoop('toolbar')}
        disabled={isCloseLoopDisabled}
        variant="flat"
        size="sm"
      />
      <IconButtonWithTooltip
        testId="button-reverse"
        icon={{ Component: Reverse }}
        title={intl.formatMessage({ defaultMessage: 'Reverse direction' })}
        onClick={() => reverse('toolbar')}
        disabled={isReverseDisabled}
        variant="flat"
        size="sm"
      />
      <div className={styles.divider} />
      <DropdownMenu
        testId="dropdown-menu-options"
        className={styles.optionsContainer}
        isOpen={isOptionsOpen}
        onCloseRequest={() => setIsOptionsOpen(false)}
        anchor={
          <button
            data-testid="button-options"
            type="button"
            className={styles.optionsButton}
            disabled={isOptionsDisabled}
            onClick={handleOpenOptions}
          >
            <SparkleFilled color={isOptionsDisabled ? '--color-text-disabled' : '--color-text-primary'} />
            <FormattedMessage defaultMessage="Options" />
            <ChevronSm orientation={isOptionsOpen ? 'up' : 'down'} color={isOptionsDisabled ? '--color-text-disabled' : '--color-text-primary'} />
          </button>
        }
        options={options}
        placement="top-start"
      />
    </div>
  );
};

export default CustomRouteToolbar;
